import React, { createContext, useContext, useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

type Language = 'en' | 'fr' | 'nl' | 'ar' | 'tr';

const SUPPORTED_LANGUAGES: Language[] = ['en', 'fr', 'nl', 'ar', 'tr'];

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  getLocalizedPath: (path: string) => string;
  switchLanguagePath: (newLang: Language) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

// Extract language from URL path
const extractLanguageFromPath = (pathname: string): Language | null => {
  const match = pathname.match(/^\/(en|tr|nl|fr|ar)(\/|$)/);
  return match ? (match[1] as Language) : null;
};

// Remove language prefix from path
const removeLanguagePrefix = (pathname: string): string => {
  return pathname.replace(/^\/(en|tr|nl|fr|ar)(?=\/|$)/, '') || '/';
};

// Preferred language: saved choice first, then browser language.
const detectPreferredLanguage = (): Language => {
  const saved = localStorage.getItem('language');
  if (saved && SUPPORTED_LANGUAGES.includes(saved as Language)) return saved as Language;
  const code = (navigator.language || 'en').split('-')[0].toLowerCase();
  const map: Record<string, Language> = { en: 'en', fr: 'fr', nl: 'nl', ar: 'ar', tr: 'tr', be: 'nl', de: 'nl' };
  return map[code] ?? 'en';
};

// Crawlers must always get the URL they requested so English pages stay indexed.
const isCrawler = (): boolean =>
  /bot|crawl|spider|slurp|facebookexternalhit|whatsapp|lighthouse|headless/i.test(navigator.userAgent);

export const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();

  const [language, setLanguageState] = useState<Language>(
    () => extractLanguageFromPath(location.pathname) ?? 'en',
  );

  // Auto-redirect unprefixed (English) URLs to the visitor's language, once per mount.
  useEffect(() => {
    if (extractLanguageFromPath(location.pathname) || isCrawler()) return;
    const preferred = detectPreferredLanguage();
    if (preferred === 'en') return;
    const clean = removeLanguagePrefix(location.pathname);
    const target = clean === '/' ? `/${preferred}/` : `/${preferred}${clean}`;
    navigate(`${target}${location.search}${location.hash}`, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const urlLang = extractLanguageFromPath(location.pathname) ?? 'en';
    if (urlLang !== language) {
      setLanguageState(urlLang);
      localStorage.setItem('language', urlLang);
    }
  }, [location.pathname, language]);

  const setLanguage = (lang: Language) => {
    setLanguageState(lang);
    localStorage.setItem('language', lang);
    document.documentElement.lang = lang;
    document.documentElement.dir = lang === 'ar' ? 'rtl' : 'ltr';
  };

  // Generate a localized path for internal links.
  const getLocalizedPath = (path: string): string => {
    const cleanPath = removeLanguagePrefix(path);
    if (language === 'en') return cleanPath;
    return cleanPath === '/' ? `/${language}/` : `/${language}${cleanPath}`;
  };

  // Path for a target language, used by the switcher. Keeps the trailing
  // slash on the homepage so it matches sitemap.xml and the canonical URL.
  const switchLanguagePath = (newLang: Language): string => {
    const currentPath = removeLanguagePrefix(location.pathname);
    if (newLang === 'en') {
      return currentPath === '/' ? '/' : currentPath;
    }
    return currentPath === '/' ? `/${newLang}/` : `/${newLang}${currentPath}`;
  };

  useEffect(() => {
    document.documentElement.lang = language;
    document.documentElement.dir = language === 'ar' ? 'rtl' : 'ltr';
  }, [language]);

  return (
    <LanguageContext.Provider value={{ language, setLanguage, getLocalizedPath, switchLanguagePath }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within LanguageProvider');
  }
  return context;
};
